/**
 * The client-wide request timeout (#507) and the one place its budgets live.
 *
 * Without one, a hung request leaves every control that waits on it dead — the
 * rule builder refuses to close while a save, count or backfill is pending —
 * until the browser gives up, minutes later.
 *
 * The budget runs until the response HEADERS arrive (time to first byte), not
 * until the body is read; and it starts after the `onRequest` middlewares, so a
 * hung token refresh is outside it. The server's own limit (Cloud Run, 300s) is
 * longer than every budget here, so the client always gives up first and a
 * timed-out write may still commit.
 *
 * **A timeout is a network failure.** `RequestTimeoutError` extends `TypeError`,
 * which is what `fetch` itself throws when no response arrives. Every screen
 * already treats a thrown non-HTTP error as "no response" (autosave maps it to
 * `SaveError(0)` → `network`, which `mayHaveCommitted` — so a timed-out PATCH
 * is re-read, because the server may have applied it). No screen learns a new
 * error — except where a write can commit after the client has left (a rule create):
 * there the screen re-reads, see `useSaveRule`. A CALLER's abort is not a timeout and stays a plain `AbortError`:
 * TanStack Query cancels queries that way and treats it as cancellation.
 */

export type RequestClass = 'read' | 'write' | 'long'

/**
 * - `read`: GETs. Slow on a bad mobile link, never legitimately slow on the
 *   server; 15s.
 * - `write`: ordinary mutations; 30s, twice a read, because the server may do
 *   a transaction plus audit work.
 * - `long`: work that can legitimately run for a minute — a rule save or apply
 *   (backfills history, up to 90 days by default), report export, and aggregator
 *   link/sync (a bank round trip). 120s.
 */
export const TIMEOUT_MS: Record<RequestClass, number> = {
  read: 15_000,
  write: 30_000,
  long: 120_000,
}

/** Paths whose work can run long, whatever the verb. Matched against the pathname. Exported for the schema guard. */
export const LONG_PATHS: readonly RegExp[] = [
  /^\/api\/v1\/reports\/export$/,
  // reauth/complete is a provider round trip; Flinks' own per-call limit is 30s, so a 30s client
  // budget would leave just as the backend's error arrives.
  /^\/api\/v1\/aggregator\/(link-session|items\/[^/]+\/(sync|relink|reauth\/complete))(\/|$)/,
  /^\/api\/v1\/recurring\/sync$/,
  // The count runs the same engine pass as the apply it previews; the undo reverts a whole batch row by row.
  /^\/api\/v1\/rules\/[^/]+\/apply\/preview$/,
  /^\/api\/v1\/rule-logs\/batch\/[^/]+\/revert$/,
]
/** Rule writes and applies backfill history on the server. Reads of rules are plain reads. */
const LONG_WRITE_PATHS: readonly RegExp[] = [/^\/api\/v1\/rules(\/|$)/]

export function classify(method: string, pathname: string): RequestClass {
  const m = method.toUpperCase()
  const isRead = m === 'GET' || m === 'HEAD'
  if (LONG_PATHS.some((re) => re.test(pathname))) return 'long'
  if (!isRead && LONG_WRITE_PATHS.some((re) => re.test(pathname))) return 'long'
  return isRead ? 'read' : 'write'
}

export class RequestTimeoutError extends TypeError {
  readonly timeoutMs: number
  constructor(timeoutMs: number) {
    super(`Request timed out after ${timeoutMs}ms`)
    this.name = 'RequestTimeoutError'
    this.timeoutMs = timeoutMs
  }
}

/**
 * Signal that aborts when either input does; falls back where `AbortSignal.any` is missing
 * (Safari before 17.4). The fallback's listener stays on the caller's signal: TanStack's is
 * per-query, so it is collected with the query.
 */
export function anySignal(a: AbortSignal, b: AbortSignal): AbortSignal {
  if (typeof AbortSignal.any === 'function') return AbortSignal.any([a, b])
  const ctl = new AbortController()
  const forward = (s: AbortSignal) => () => ctl.abort(s.reason)
  for (const s of [a, b]) {
    if (s.aborted) {
      ctl.abort(s.reason)
      break
    }
    s.addEventListener('abort', forward(s), { once: true })
  }
  return ctl.signal
}

/**
 * A `fetch` that enforces the budget. The timer is our own rather than
 * `AbortSignal.timeout`, so it can be cleared on settle (no stray timer per
 * request) and so the cause is known without reading `reason`.
 */
export function withTimeout(
  // Captured at construction, exactly as openapi-fetch's own default is. Reading `globalThis.fetch`
  // per request would make a late `vi.stubGlobal('fetch')` start intercepting every module's calls,
  // which `Welcome.test.tsx` documents the suite does not rely on.
  baseFetch: typeof fetch = globalThis.fetch,
): (request: Request) => Promise<Response> {
  return async (request) => {
    const ms = TIMEOUT_MS[classify(request.method, new URL(request.url).pathname)]
    const timer = new AbortController()
    let timedOut = false
    const id = setTimeout(() => {
      timedOut = true
      timer.abort()
    }, ms)
    try {
      return await baseFetch(new Request(request, { signal: anySignal(request.signal, timer.signal) }))
    } catch (e) {
      // Only when the caller did not abort first: their abort wins and stays an AbortError.
      if (timedOut && !request.signal.aborted) throw new RequestTimeoutError(ms)
      throw e
    } finally {
      clearTimeout(id)
    }
  }
}
